import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Sites = ({ onSubmit, initialData }) => {
  const { t } = useTranslation();
  const [sites, setSites] = useState([]);
  const [entities, setEntities] = useState([]);
  const [errors] = useState({});
  const [selectedEntity, setSelectedEntity] = useState('');

  const countries = [
    { code: 'PL', name: t('workflow.scopeBoundaries.legalEntities.countries.PL') },
    { code: 'DE', name: t('workflow.scopeBoundaries.legalEntities.countries.DE') },
    { code: 'US', name: t('workflow.scopeBoundaries.legalEntities.countries.US') },
    { code: 'GB', name: t('workflow.scopeBoundaries.legalEntities.countries.GB') },
    { code: 'FR', name: t('workflow.scopeBoundaries.legalEntities.countries.FR') },
    { code: 'IT', name: t('workflow.scopeBoundaries.legalEntities.countries.IT') },
    { code: 'ES', name: t('workflow.scopeBoundaries.legalEntities.countries.ES') },
    { code: 'NL', name: t('workflow.scopeBoundaries.legalEntities.countries.NL') },
    { code: 'BE', name: t('workflow.scopeBoundaries.legalEntities.countries.BE') },
    { code: 'AT', name: t('workflow.scopeBoundaries.legalEntities.countries.AT') }
  ];

  const siteTypes = [
    { value: 'plant', label: t('workflow.scopeBoundaries.sites.siteTypes.plant') },
    { value: 'office', label: t('workflow.scopeBoundaries.sites.siteTypes.office') },
    { value: 'warehouse', label: t('workflow.scopeBoundaries.sites.siteTypes.warehouse') },
    { value: 'retail', label: t('workflow.scopeBoundaries.sites.siteTypes.retail') },
    { value: 'datacenter', label: t('workflow.scopeBoundaries.sites.siteTypes.datacenter') },
    { value: 'fleet_depot', label: t('workflow.scopeBoundaries.sites.siteTypes.fleet_depot') },
    { value: 'other', label: t('workflow.scopeBoundaries.sites.siteTypes.other') }
  ];

  const ownershipTypes = [
    { value: 'owned', label: t('workflow.scopeBoundaries.sites.ownershipTypes.owned') },
    { value: 'rented', label: t('workflow.scopeBoundaries.sites.ownershipTypes.rented') }
  ];

  const timezones = [
    { value: 'Europe/Warsaw', label: 'Europe/Warsaw' },
    { value: 'Europe/Berlin', label: 'Europe/Berlin' },
    { value: 'Europe/London', label: 'Europe/London' },
    { value: 'Europe/Paris', label: 'Europe/Paris' },
    { value: 'America/New_York', label: 'America/New_York' },
    { value: 'America/Los_Angeles', label: 'America/Los_Angeles' },
    { value: 'Asia/Tokyo', label: 'Asia/Tokyo' },
    { value: 'Australia/Sydney', label: 'Australia/Sydney' }
  ];

  // Load entities from localStorage
  useEffect(() => {
    const savedEntities = localStorage.getItem('scopeBoundaries_legalEntities');
    if (savedEntities) {
      try {
        const parsedData = JSON.parse(savedEntities);
        if (parsedData.entities && parsedData.entities.length > 0) {
          setEntities(parsedData.entities);
          setSelectedEntity(parsedData.entities[0].id);
        }
      } catch (error) {
        console.error('Error loading entities:', error);
      }
    }
  }, []);

  // Load sites from localStorage
  useEffect(() => {
    const savedSites = localStorage.getItem('scopeBoundaries_sites');
    if (savedSites) {
      try {
        const parsedData = JSON.parse(savedSites);
        if (parsedData.sites && parsedData.sites.length > 0) {
          setSites(parsedData.sites);
        }
      } catch (error) {
        console.error('Error loading sites:', error);
      }
    }
  }, []);

  const addNewSite = useCallback(() => {
    if (!selectedEntity) return;
    
    const newSite = {
      id: Date.now().toString(),
      entity_id: selectedEntity,
      site_name: '',
      site_type: 'office',
      country: '',
      region_code: '',
      city: '',
      timezone: 'Europe/Warsaw',
      grid_region: '',
      ownership_type: 'owned',
      owner_user_id: '',
      active_from: '',
      active_to: '',
      created_by: 'current_user',
      created_at: new Date().toISOString()
    };
    setSites(prev => [...prev, newSite]);
  }, [selectedEntity]);

  const updateSite = (id, field, value) => {
    setSites(sites.map(site => 
      site.id === id ? { ...site, [field]: value } : site
    ));
  };

  const removeSite = (id) => {
    // Don't allow removing the first site for each entity
    const siteToRemove = sites.find(site => site.id === id);
    if (siteToRemove) {
      const sitesForEntity = sites.filter(site => site.entity_id === siteToRemove.entity_id);
      if (sitesForEntity.length === 1) {
        alert(t('workflow.scopeBoundaries.sites.warnings.cannotRemoveLastSite'));
        return;
      }
    }
    setSites(sites.filter(site => site.id !== id));
  };

  // Validation helpers removed; WorkflowTabView manages navigation

  const getCountryWarning = (site) => {
    const entity = entities.find(e => e.id === site.entity_id);
    if (entity && site.country && entity.country && site.country !== entity.country) {
      return t('workflow.scopeBoundaries.sites.warnings.countryMismatch');
    }
    return null;
  };

  // Validation helpers kept; navigation handled at WorkflowTabView level

  // Navigation handled by WorkflowTabView; save via onSubmit externally



  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="mb-6">
        <div className="flex items-center mb-4">
          <MapPin className="h-6 w-6 text-blue-600 mr-3" />
          <h2 className="text-xl font-semibold text-gray-900">{t('workflow.scopeBoundaries.sites.title')}</h2>
        </div>
        <p className="text-gray-600">{t('workflow.scopeBoundaries.sites.description')}</p>
      </div>

      {/* Entity Filter */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('workflow.scopeBoundaries.sites.filterByEntity')}
        </label>
        <select
          value={selectedEntity}
          onChange={(e) => setSelectedEntity(e.target.value)}
          className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {entities.map(entity => (
            <option key={entity.id} value={entity.id}>
              {entity.legal_name}
            </option>
          ))}
        </select>
      </div>



      {/* Site Forms */}
      <div className="space-y-6">
        {sites.map((site, index) => (
          <div key={site.id} className="bg-gray-50 rounded-lg p-6 border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {t('workflow.scopeBoundaries.sites.siteNumber', { number: index + 1, name: site.site_name || t('workflow.scopeBoundaries.sites.addSite') })}
              </h3>
                             {(() => {
                 const sitesForEntity = sites.filter(s => s.entity_id === site.entity_id);
                 const canRemove = sitesForEntity.length > 1;
                 return (
                   <button
                     onClick={() => removeSite(site.id)}
                     disabled={!canRemove}
                     className={`p-1 ${
                       canRemove 
                         ? 'text-red-600 hover:text-red-800' 
                         : 'text-gray-400 cursor-not-allowed'
                     }`}
                     title={canRemove ? t('workflow.scopeBoundaries.sites.removeSite') : t('workflow.scopeBoundaries.sites.cannotRemoveLastSite')}
                   >
                     <Trash2 className="h-4 w-4" />
                   </button>
                 );
               })()}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Entity */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('workflow.scopeBoundaries.sites.form.entity')}
                </label>
                <select
                  value={site.entity_id}
                  onChange={(e) => updateSite(site.id, 'entity_id', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors[`${site.id}_entity_id`] ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">{t('workflow.scopeBoundaries.sites.selectEntity')}</option>
                  {entities.map(entity => (
                    <option key={entity.id} value={entity.id}>
                      {entity.legal_name}
                    </option>
                  ))}
                </select>
                {errors[`${site.id}_entity_id`] && (
                  <p className="mt-1 text-sm text-red-600">{errors[`${site.id}_entity_id`]}</p>
                )}
              </div>

              {/* Site Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('workflow.scopeBoundaries.sites.form.siteName')}
                </label>
                <input
                  type="text"
                  value={site.site_name}
                  onChange={(e) => updateSite(site.id, 'site_name', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors[`${site.id}_site_name`] ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder={t('workflow.scopeBoundaries.sites.form.siteNamePlaceholder')}
                />
                {errors[`${site.id}_site_name`] && (
                  <p className="mt-1 text-sm text-red-600">{errors[`${site.id}_site_name`]}</p>
                )}
              </div>

              {/* Site Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('workflow.scopeBoundaries.sites.form.siteType')}
                </label>
                <select
                  value={site.site_type}
                  onChange={(e) => updateSite(site.id, 'site_type', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors[`${site.id}_site_type`] ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  {siteTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
                {errors[`${site.id}_site_type`] && (
                  <p className="mt-1 text-sm text-red-600">{errors[`${site.id}_site_type`]}</p>
                )}
              </div>

                             {/* Country */}
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-1">
                   {t('workflow.scopeBoundaries.sites.form.country')}
                 </label>
                 <select
                   value={site.country}
                   onChange={(e) => updateSite(site.id, 'country', e.target.value)}
                   className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                     errors[`${site.id}_country`] ? 'border-red-500' : 'border-gray-300'
                   }`}
                 >
                   <option value="">{t('workflow.scopeBoundaries.sites.form.selectCountry')}</option>
                   {countries.map(country => (
                     <option key={country.code} value={country.code}>
                       {country.name} ({country.code})
                     </option>
                   ))}
                 </select>
                 {errors[`${site.id}_country`] && (
                   <p className="mt-1 text-sm text-red-600">{errors[`${site.id}_country`]}</p>
                 )}
                 {getCountryWarning(site) && (
                   <p className="mt-1 text-sm text-yellow-600">{getCountryWarning(site)}</p>
                 )}
               </div>

              {/* Region Code */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('workflow.scopeBoundaries.sites.form.regionCode')}
                </label>
                <input
                  type="text"
                  value={site.region_code}
                  onChange={(e) => updateSite(site.id, 'region_code', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t('workflow.scopeBoundaries.sites.form.regionCodePlaceholder')}
                />
              </div>

              {/* City */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('workflow.scopeBoundaries.sites.form.city')}
                </label>
                <input
                  type="text"
                  value={site.city}
                  onChange={(e) => updateSite(site.id, 'city', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t('workflow.scopeBoundaries.sites.form.cityPlaceholder')}
                />
              </div>

              {/* Timezone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('workflow.scopeBoundaries.sites.form.timezone')}
                </label>
                <select
                  value={site.timezone}
                  onChange={(e) => updateSite(site.id, 'timezone', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors[`${site.id}_timezone`] ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  {timezones.map(tz => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </select>
                {errors[`${site.id}_timezone`] && (
                  <p className="mt-1 text-sm text-red-600">{errors[`${site.id}_timezone`]}</p>
                )}
              </div>

              {/* Grid Region */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('workflow.scopeBoundaries.sites.form.gridRegion')}
                </label>
                <input
                  type="text"
                  value={site.grid_region}
                  onChange={(e) => updateSite(site.id, 'grid_region', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t('workflow.scopeBoundaries.sites.form.gridRegionPlaceholder')}
                />
              </div>

              {/* Ownership Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('workflow.scopeBoundaries.sites.form.ownershipType')}
                </label>
                <select
                  value={site.ownership_type}
                  onChange={(e) => updateSite(site.id, 'ownership_type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {ownershipTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Owner User ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('workflow.scopeBoundaries.sites.form.ownerUserId')}
                </label>
                <input
                  type="text"
                  value={site.owner_user_id}
                  onChange={(e) => updateSite(site.id, 'owner_user_id', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t('workflow.scopeBoundaries.sites.form.ownerUserIdPlaceholder')}
                />
              </div>

              {/* Active From */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('workflow.scopeBoundaries.sites.form.activeFrom')}
                </label>
                <input
                  type="date"
                  value={site.active_from}
                  onChange={(e) => updateSite(site.id, 'active_from', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Active To */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('workflow.scopeBoundaries.sites.form.activeTo')}
                </label>
                <input
                  type="date"
                  value={site.active_to}
                  onChange={(e) => updateSite(site.id, 'active_to', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Helper action (not navigation) */}
      <div className="mt-8 flex justify-end">
        <button
          onClick={addNewSite}
          disabled={!selectedEntity}
          className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-md ${
            selectedEntity
              ? 'text-white bg-green-600 border border-transparent hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500'
              : 'text-gray-400 bg-gray-100 border border-gray-200 cursor-not-allowed'
          }`}
        >
          <Plus className="h-4 w-4 mr-2" />
          {t('workflow.scopeBoundaries.sites.addSite')}
        </button>
      </div>
    </div>
  );
};

export default Sites;
